import React, { useState } from 'react';
import { css } from '@emotion/css';
import appLabels from '../../utils/appLabels';
import UserMessage from '../../common/UserMessage/UserMessage';
import LoadingComponent from '../../common/LoadingComponent/LoadingComponent';
import CancelledItem from '../ScheduledItems/CancelledItem';
import PropTypes from 'prop-types';

const generalStyle = css`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const CancelledItemsListStyle = css`
  display: flex;
  flex-direction: column;
  justify-items: center;
  width: 100%;
`;

const showButtonStyle = css`
  cursor: pointer;
  display: flex;
  justify-content: center;
  border-radius: 6.25rem;
  border: 0.0625rem solid #7b1a27;
  background: #fff;
  padding-left: 1rem;
  padding-right: 1rem;
  width: fit-content;
  height: 2.5rem;
  margin-top: 1.5rem;
  align-items: center;
  align-self: center;
`;

const buttonTextStyle = css`
  margin-right: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3125rem;
  color: #7b1a27;
`;
const titleSectionStyle = css`
  margin-top: 1.875rem;
  display: flex;
  margin-bottom: 1rem;
`;

const titleTextStyle = css`
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.5rem;
  margin-right: 1.5rem;
`;

const subTitleTextStyle = css`
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
  align-self: center;
`;

const showTenMoreSectionStyle = css`
  display: flex;
  flex-direction: column;
`;

const footerTextStyle = css`
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
  align-self: center;
  margin-top: 1.5rem;
`;

// method to calculate number of months between the older cancel date and the current date
const getNumberOfMonths = (cancelledDate: string): number => {
  const cancelledDateObject = new Date(cancelledDate);
  const currentDateObject = new Date();
  const numberOfMonths =
    (currentDateObject.getFullYear() - cancelledDateObject.getFullYear()) * 12 +
    (currentDateObject.getMonth() - cancelledDateObject.getMonth());
  return numberOfMonths;
};

const CancelledItemsList = ({ loading = false, items, refreshHandler = () => {}, apiError = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExtended, setIsExtended] = useState(false);
  const [increment, setIncrement] = useState(0);
  const base = 5;
  const sliceIterval = isExtended ? base + increment : base;
  const stillRemaining = items.length > sliceIterval;
  const cancelledItemsList = items
    .slice(0, sliceIterval)
    .map((item) => <CancelledItem item={item} key={item.id} isCancelledItem={true} />);

  const expandButtonTex = isExpanded
    ? appLabels.CancelledItemsList.hideCancelledText
    : appLabels.CancelledItemsList.showCancelledText;
  const emptyList = items.length === 0;
  const title = !emptyList ? appLabels.CancelledItemsList.title : appLabels.CancelledItemsList.emptyListTitle;
  // sort items by cancelDate
  items.sort((a, b) => new Date(b.cancelDate).getTime() - new Date(a.cancelDate).getTime());
  const numberOfMonths = !emptyList ? getNumberOfMonths(items[items.length - 1].cancelledDate) : 0;
  const monthsFormat = {
    numberOfMonths: numberOfMonths || '',
    plural: numberOfMonths > 1 ? 's' : '',
  };
  return (
    <div className={generalStyle}>
      <button
        aria-roledescription="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        className={showButtonStyle}
      >
        <div className={buttonTextStyle}>{expandButtonTex}</div>
        <div>
          {isExpanded ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.46967 14.5303C5.76256 14.8232 6.23744 14.8232 6.53033 14.5303L12 9.06066L17.4697 14.5303C17.7626 14.8232 18.2374 14.8232 18.5303 14.5303C18.8232 14.2374 18.8232 13.7626 18.5303 13.4697L12.5303 7.46967C12.2374 7.17678 11.7626 7.17678 11.4697 7.46967L5.46967 13.4697C5.17678 13.7626 5.17678 14.2374 5.46967 14.5303Z"
                fill="#7B1A27"
              />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.5303 9.46967C18.2374 9.17678 17.7626 9.17678 17.4697 9.46967L12 14.9393L6.53033 9.46967C6.23744 9.17678 5.76256 9.17678 5.46967 9.46967C5.17678 9.76256 5.17678 10.2374 5.46967 10.5303L11.4697 16.5303C11.7626 16.8232 12.2374 16.8232 12.5303 16.5303L18.5303 10.5303C18.8232 10.2374 18.8232 9.76256 18.5303 9.46967Z"
                fill="#7B1A27"
              />
            </svg>
          )}
        </div>
      </button>
      {isExpanded && loading && <LoadingComponent />}
      {isExpanded && !loading && (
        <div>
          {!loading && apiError && (
            <UserMessage messageText={appLabels.ApiErrorMessages.cancelledItemList} messageType={'warning'} />
          )}
          {!apiError && (
            <div>
              <div className={titleSectionStyle}>
                <div className={titleTextStyle}>{title}</div>
                {!emptyList && <div className={subTitleTextStyle}>{appLabels.CancelledItemsList.subTitle}</div>}
              </div>
              <div className={CancelledItemsListStyle}>{cancelledItemsList}</div>

              <div className={showTenMoreSectionStyle}>
                {stillRemaining && !emptyList && (
                  <button
                    onClick={() => {
                      setIncrement(increment + 10);
                      setIsExtended(true);
                    }}
                    className={showButtonStyle}
                  >
                    <div className={buttonTextStyle}>{appLabels.CancelledItemsList.showTenMoreText}</div>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M18.5303 9.46967C18.2374 9.17678 17.7626 9.17678 17.4697 9.46967L12 14.9393L6.53033 9.46967C6.23744 9.17678 5.76256 9.17678 5.46967 9.46967C5.17678 9.76256 5.17678 10.2374 5.46967 10.5303L11.4697 16.5303C11.7626 16.8232 12.2374 16.8232 12.5303 16.5303L18.5303 10.5303C18.8232 10.2374 18.8232 9.76256 18.5303 9.46967Z"
                        fill="#7B1A27"
                      />
                    </svg>
                  </button>
                )}
                {!stillRemaining && <div className={footerTextStyle}>{appLabels.CancelledItemsList.footerText}</div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

CancelledItemsList.propTypes = {
  children: PropTypes.any,
  items: PropTypes.array.isRequired,
};

export default CancelledItemsList;
